import { normalizeAuthoringGoalQuery, normalizeCoachRecapQuery } from './authoringQueries';
import { coachingConsumerRequest } from './consumerQueries';
import type { CoachingConsumerPage, CoachingConsumerSelection } from './consumerQueries';
import type { components, operations } from './api.generated';
import { pinnedScope, scopeQuery } from './scope';
import type { CoachingScopeIntent } from './scope';

export const CONTRACT_VERSION = 'coaching-companion.v1' as const;

/** Authentication is supplied by the separate companion auth integration. */
export interface CoachingTransport {
  request<T>(request: {
    method: 'GET' | 'POST' | 'PUT' | 'PATCH' | 'DELETE';
    path: string;
    /** Array values serialize as repeated query keys, never comma coercion. */
    query: Record<string, string | readonly string[]>;
    body?: unknown;
  }): Promise<T>;
}

/** Wire integers stay integers. A JS consumer must reject unsafe int64s. */
export function assertSafeWireNumbers(value: unknown): void {
  if (typeof value === 'number' && (!Number.isFinite(value) || (Number.isInteger(value) && !Number.isSafeInteger(value)))) {
    throw new RangeError('unsafe_wire_number');
  }
  if (Array.isArray(value)) {
    for (const child of value) assertSafeWireNumbers(child);
  } else if (value !== null && typeof value === 'object') {
    for (const child of Object.values(value)) assertSafeWireNumbers(child);
  }
}

export const KNOWN_DOMAIN_CODES = [
  'manual_request_invalid', 'manual_auth_required', 'manual_access_required', 'manual_csrf_required',
  'manual_scope_invalid', 'manual_unavailable', 'manual_version_conflict', 'manual_operation_conflict',
  'manual_operation_retired', 'manual_operation_unresolved', 'manual_source_changed',
  'manual_reference_unsupported', 'manual_retry_required', 'manual_read_failed', 'manual_write_failed',
  'consumer_request_invalid', 'consumer_auth_required', 'consumer_access_required', 'consumer_scope_invalid',
  'consumer_unavailable', 'consumer_cursor_invalid', 'consumer_query_refresh_required',
  'consumer_dependency_unavailable', 'consumer_retry_required', 'consumer_read_failed',
  'invalid_scope_selection', 'conflicting_season_selection', 'season_selection_unsupported',
  'activity_unavailable', 'context_unavailable', 'coaching_access_required',
  'record_unavailable', 'record_expired', 'draft_unavailable',
  'source_unavailable', 'source_revision_conflict', 'source_policy_conflict',
  'revision_conflict', 'operation_conflict', 'operation_retired', 'invalid_transition',
  'confirmation_unavailable', 'confirmation_revision_conflict', 'publication_preview_stale',
  'publication_confirmation_required', 'drafting_disabled', 'ai_processing_blocked',
  'evidence_stale', 'evidence_unavailable', 'evidence_retry_required',
  'job_unavailable', 'job_expired', 'result_unavailable', 'policy_changed',
  'database_retry_required', 'external_allocation_unavailable',
  'provider_not_configured',
  'collection_request_invalid', 'collection_auth_required', 'collection_access_required', 'collection_scope_invalid',
  'collection_unavailable', 'collection_cursor_invalid', 'collection_query_refresh_required',
  'collection_retry_required', 'collection_read_failed',
  'recap_request_invalid', 'recap_auth_required', 'recap_access_required', 'recap_csrf_required',
  'recap_delete_denied', 'recap_unavailable', 'recap_scope_invalid', 'recap_version_conflict',
  'recap_version_exhausted', 'recap_retry_required', 'recap_read_failed', 'recap_write_failed',
  'recap_cursor_invalid', 'recap_query_refresh_required',
  'player_role_required', 'self_player_required', 'self_player_ambiguous', 'player_inactive',
  'family_own_read_required', 'operation_unresolved', 'served_content_proof_required',
  'approval_artifact_unavailable', 'approved_action_unavailable',
  'supporting_observation_required',
] as const;
export type CoachingDomainCode = typeof KNOWN_DOMAIN_CODES[number];
export type CoachingClientFailure = {
  status: number | null;
  category: 'domain' | 'authentication' | 'authorization' | 'unavailable' | 'validation' | 'transport' | 'unknown';
  code: CoachingDomainCode | null;
};

/** Normalize existing detail-object/string/array envelopes without echoing them. */
export function normalizeFailure(status: number | null, envelope: unknown): CoachingClientFailure {
  if (status === null) return { status, category: 'transport', code: null };
  const detail = envelope !== null && typeof envelope === 'object' && 'detail' in envelope
    ? envelope.detail : null;
  const code = detail !== null && typeof detail === 'object' && 'code' in detail
    && typeof detail.code === 'string' && (KNOWN_DOMAIN_CODES as readonly string[]).includes(detail.code)
    ? detail.code as CoachingDomainCode : null;
  if (code) return { status, category: 'domain', code };
  const category = status === 401 ? 'authentication' : status === 403 ? 'authorization'
    : status === 404 || status === 410 || status === 503 ? 'unavailable' : status === 422 ? 'validation' : 'unknown';
  return { status, category, code: null };
}

type Schema = components['schemas'];
type Result = Schema['CoachingDraftEnvelope'] | Schema['CoachingAIOutputEnvelope'];

export function coachingClient(transport: CoachingTransport, intent: CoachingScopeIntent) {
  const query = scopeQuery(pinnedScope(intent));
  async function send<T>(method: 'GET' | 'POST' | 'PUT' | 'PATCH' | 'DELETE', path: string, body?: unknown): Promise<T> {
    assertSafeWireNumbers(body);
    const response = await transport.request<T>({ method, path, query: { ...query }, body });
    assertSafeWireNumbers(response);
    return response;
  }
  const id = encodeURIComponent;
  return {
    capabilities: () => send<Schema['CoachingCompanionCapabilities']>('GET', '/api/coach/companion/capabilities'),
    createManualNote: (body: Schema['CreateCoachingNoteRequest']) => send<Schema['CoachingManualNoteEnvelope']>('POST', '/api/coach/notes', body),
    createManualGoal: (body: Schema['CreatePlayerGoalRequest']) => send<Schema['CoachingManualGoalEnvelope']>('POST', '/api/coach/goals', body),
    createActivity: (body: Schema['CreateCoachingActivityRequest']) => send<Schema['CoachingActivityEnvelope']>('POST', '/api/coach/activities', body),
    getActivities: () => send<Schema['CoachingActivityListEnvelope']>('GET', '/api/coach/activities'),
    createCapture: (body: Schema['CreateCoachingCaptureRequest']) => send<Schema['CoachingCaptureEnvelope']>('POST', '/api/coach/captures', body),
    getCapture: (captureId: string) => send<Schema['CoachingCaptureEnvelope']>('GET', `/api/coach/captures/${id(captureId)}`),
    getCaptures: () => send<Schema['CoachingCaptureListEnvelope']>('GET', '/api/coach/captures'),
    discardCapture: (captureId: string, body: Schema['DiscardCoachingCaptureRequest']) => send<Schema['CoachingCaptureEnvelope']>('PATCH', `/api/coach/captures/${id(captureId)}/status`, body),
    preview: (captureId: string, body: Schema['CoachingPublicationSelection']) => send<Schema['CoachingPublicationPreview']>('POST', `/api/coach/captures/${id(captureId)}/publication-preview`, body),
    confirm: (captureId: string, body: Schema['CreateCoachingConfirmationRequest']) => send<Schema['CoachingConfirmationResponse']>('POST', `/api/coach/captures/${id(captureId)}/publication-confirmations`, body),
    generateConfirmed: (confirmationId: string, body: Schema['GenerateConfirmedCoachingDraftRequest']) => send<Schema['CoachingAIJobProgress']>('POST', `/api/coach/publication-confirmations/${id(confirmationId)}/generate`, body),
    getJob: (jobId: string) => send<Schema['CoachingAIJobEnvelope']>('GET', `/api/coach/ai/jobs/${id(jobId)}`),
    cancelJob: (jobId: string) => send<Schema['CoachingAIJobEnvelope']>('POST', `/api/coach/ai/jobs/${id(jobId)}/cancel`),
    getResult: (jobId: string) => send<Result>('GET', `/api/coach/ai/jobs/${id(jobId)}/result`),
    getDraft: (draftId: string) => send<Schema['CoachingDraftEnvelope']>('GET', `/api/coach/application-drafts/${id(draftId)}`),
    approve: (draftId: string, body: Schema['ApproveConfirmedCoachingDraftRequest']) => send<Schema['CoachingApprovalResponse']>('POST', `/api/coach/application-drafts/${id(draftId)}/approve`, body),
    approvePrivateFeedback: (draftId: string, body: Schema['ApprovePrivateCoachingFeedbackRequest']) => send<Schema['PrivateCoachingFeedbackAcceptedResponse']>('POST', `/api/coach/application-drafts/${id(draftId)}/approve`, body),
    timeline: (playerId: string) => send<Schema['CoachingEvidenceTimelineResponse']>('GET', `/api/coach/players/${id(playerId)}/evidence-timeline`),
    followup: (playerId: string, body: Schema['RecordCoachingFollowupRequest']) => send<Schema['CoachingFollowupResponse']>('POST', `/api/coach/players/${id(playerId)}/followups`, body),
    resolveFocus: (activityId: string, body: Schema['ResolveCoachingActivityFocusReferenceRequest']) => send<Schema['CoachingActivityFocusReferenceEnvelope']>('POST', `/api/coach/activities/${id(activityId)}/focus-reference`, body),
    selectFocus: (activityId: string, playerId: string, body: Schema['SelectCoachingActivityFocusRequest']) => send<Schema['CoachingActivityFocusEnvelope']>('PUT', `/api/coach/activities/${id(activityId)}/focuses/${id(playerId)}`, body),
    getFocuses: (activityId: string) => send<Schema['CoachingActivityFocusListEnvelope']>('GET', `/api/coach/activities/${id(activityId)}/focuses`),
  };
}

/** Explicit provider-free operations. No call advances another command. */
export function coachingManualClient(transport: CoachingTransport, intent: CoachingScopeIntent) {
  const query = scopeQuery(pinnedScope(intent));
  // Manual writes pin the actual season, never the mutable current-season alias.
  if (intent.season.mode === 'current') delete query.season_scope;
  const id = (value: string, limit = 128) => {
    if (typeof value !== 'string' || !value || [...value].length > limit) throw new RangeError('invalid_manual_identity');
    return encodeURIComponent(value);
  };
  const uuid = (value: string) => {
    if (typeof value !== 'string' || !/^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(value)) {
      throw new RangeError('invalid_manual_identity');
    }
    return value;
  };
  async function send<T>(method: 'GET' | 'POST' | 'PUT', path: string, body?: unknown, extra?: Record<string, string>): Promise<T> {
    assertSafeWireNumbers(body);
    const response = await transport.request<T>({ method, path, query: { ...query, ...extra }, body });
    assertSafeWireNumbers(response);
    return response;
  }
  const activity = (key: string) => `/api/coach/activities/${id(key)}`;
  const support = (key: string) => `/api/coach/observation-supports/${uuid(key)}`;
  return {
    createGoalFromFeedback: (body: Schema['CreateGoalFromFeedbackRequest']) =>
      send<Schema['GoalFromFeedbackResult']>('POST', '/api/coach/goals', body),
    resolveFocus: (activityId: string, body: Schema['ResolveCanonicalFocusRequest']) =>
      send<Schema['CanonicalFocusReferenceResponse']>('POST', activity(activityId) + '/canonical-focus-reference', body),
    listFocuses: (activityId: string) => send<Schema['CanonicalFocusListResponse']>('GET', activity(activityId) + '/canonical-focuses'),
    selectFocus: (activityId: string, playerId: string, body: Schema['SelectCanonicalFocusRequest']) =>
      send<Schema['CanonicalFocusResponse']>('PUT', activity(activityId) + `/canonical-focuses/${id(playerId)}`, body),
    createSupport: (body: Schema['CreateAuthoredSupportRequest']) =>
      send<Schema['AuthoredSupportResult']>('POST', '/api/coach/observation-supports', body),
    correctSupport: (supportId: string, body: Schema['CorrectAuthoredSupportRequest']) =>
      send<Schema['AuthoredSupportResult']>('PUT', support(supportId), body),
    readSupport: (supportId: string, revisionId: string, playerId: string, eventId?: string) => {
      id(playerId, 200);
      return send<Schema['ManualSupportResponse']>('GET', support(supportId) + `/revisions/${uuid(revisionId)}`,
        undefined, { player_id: playerId, ...(eventId === undefined ? {} : { event_id: uuid(eventId) }) });
    },
    recordSupportEvent: (supportId: string, body: Schema['RecordSupportEventRequest']) =>
      send<Schema['SupportEventResult']>('POST', support(supportId) + '/events', body),
    recordFollowup: (playerId: string, body: Schema['RecordCanonicalFollowupRequest']) =>
      send<Schema['CanonicalFollowupResult']>('POST', `/api/coach/players/${id(playerId)}/canonical-followups`, body),
  };
}

/** Standalone recap transport; caller supplies authentication and deliberate scope. */
export function recapClient(transport: CoachingTransport, intent: CoachingScopeIntent) {
  const query = scopeQuery(pinnedScope(intent));
  function path(id: string) {
    if (typeof id !== 'string' || !/^[1-9][0-9]{0,18}$/.test(id) || BigInt(id) > 9223372036854775807n) throw new RangeError('invalid_recap_id');
    return `/api/recaps/${id}`;
  }
  async function send<T>(method: 'GET' | 'POST' | 'PUT' | 'DELETE', target: string, body?: unknown, view?: 'coach' | 'recipient'): Promise<T> {
    assertSafeWireNumbers(body);
    const result = await transport.request<T>({ method, path: target, query: { ...query, ...(view ? { view } : {}) }, body });
    assertSafeWireNumbers(result); return result;
  }
  return {
    collection: async (filters: { limit?: number; cursor?: string | null } = {}) => {
      const normalized = normalizeCoachRecapQuery(filters);
      const result = await transport.request<Schema['RecapCollectionResponse']>({ method:'GET', path:'/api/recaps',
        query:{ ...query, view:'coach', limit:String(normalized.limit), ...(normalized.cursor !== null ? { cursor:normalized.cursor } : {}) } });
      assertSafeWireNumbers(result); return result;
    },
    create: (body: Schema['RecapCreateRequest']) => send<Schema['RecapWriteResult']>('POST', '/api/recaps', body),
    detail: (id: string, view: 'coach' | 'recipient') => send<Schema['RecapCoachDetail'] | Schema['RecapRecipientDetail']>('GET', path(id), undefined, view),
    edit: (id: string, body: Schema['RecapEditRequest']) => send<Schema['RecapWriteResult']>('PUT', path(id), body),
    delete: (id: string, body: Schema['RecapDeleteRequest']) => send<Schema['RecapDeleteResult']>('DELETE', path(id), body),
    acknowledgments: (id: string) => send<Schema['RecapAcknowledgmentsResponse']>('GET', path(id) + '/acknowledgments'),
    seen: (id: string, body: Schema['RecapSeenRequest']) => send<Schema['RecapSeenResult']>('PUT', path(id) + '/seen', body),
    ownReading: (id: string) => send<Schema['RecapOwnReadingResponse']>('GET', path(id) + '/own-reading'),
    fullView: (id: string, body: Schema['RecapFullViewRequest']) => send<Schema['RecapFullViewResponse']>('POST', path(id) + '/full-view', body),
    ownReadingProof: (id: string, body: Schema['RecapOwnReadProofRequest']) => send<Schema['RecapOwnReadProofResponse']>('POST', path(id) + '/own-reading-proof', body),
    markOwnReading: (id: string, body: Schema['RecapOwnReadRequest']) => send<Schema['RecapOwnReadResult']>('PUT', path(id) + '/own-reading', body),
  };
}

/** Explicit subject choices; Goal identities are exact decimal strings. */
export function feedbackGoalChoicesClient(transport: CoachingTransport, intent: CoachingScopeIntent) {
  const scope = scopeQuery(pinnedScope(intent));
  return async (query: { player_ids: string[]; limit?: number; cursor?: string | null }) => {
    const normalized = normalizeAuthoringGoalQuery(query);
    const result = await transport.request<Schema['FeedbackGoalChoicesResponse']>({ method:'GET',path:'/api/coach/feedback-goals',
      query:{ ...scope, player_ids:normalized.player_ids, limit:String(normalized.limit),
        ...(normalized.cursor !== null ? { cursor:normalized.cursor } : {}) } });
    assertSafeWireNumbers(result); return result;
  };
}

/** Three coach reads, with exact decimal identities and explicit selected scope. */
export function coachingConsumerClient(transport: CoachingTransport, intent: CoachingScopeIntent) {
  const scope = scopeQuery(pinnedScope(intent));
  async function read<T>(selection: CoachingConsumerSelection, controls: CoachingConsumerPage): Promise<T> {
    const request = coachingConsumerRequest(selection, controls);
    const result = await transport.request<T>({ method: 'GET', path: request.path, query: { ...scope, ...request.query } });
    assertSafeWireNumbers(result);
    return result;
  }
  return {
    goalSupport: (goalId: string, playerId: string, query: CoachingConsumerPage = {}) =>
      read<Schema['CoachingGoalSupportResponse']>({ kind: 'goal-support', goalId, playerId }, query),
    history: (playerId: string, query: CoachingConsumerPage = {}) =>
      read<Schema['CoachingCanonicalHistoryResponse']>({ kind: 'history', playerId }, query),
    mixedHistory: (playerId: string, query: CoachingConsumerPage = {}) =>
      read<Schema['CoachingMixedHistoryResponse']>({ kind: 'mixed-history', playerId }, query),
    candidates: (playerId: string, kind: 'action' | 'focus' | 'support', query: CoachingConsumerPage = {}) =>
      read<Schema['CoachingCanonicalCandidatesResponse']>({ kind: 'candidates', playerId, candidateKind: kind }, query),
  };
}


export type CoachFeedbackCollectionQuery = Omit<NonNullable<operations['coach_collection_api_coach_feedback_get']['parameters']['query']>,
  'team_id' | 'season_id' | 'season_scope'>;
export type RecipientFeedbackCollectionQuery = Omit<NonNullable<operations['recipient_collection_api_feedback_get']['parameters']['query']>,
  'team_id' | 'season_id' | 'season_scope'>;
/** Pure collection client; deliberate scope cannot be overridden by controls. */
export function feedbackCollectionClient(transport: CoachingTransport, intent: CoachingScopeIntent) {
  const scope = scopeQuery(pinnedScope(intent));
  async function read<T>(path: string, filters: CoachFeedbackCollectionQuery | RecipientFeedbackCollectionQuery): Promise<T> {
    assertSafeWireNumbers(filters);
    const query: Record<string, string> = {};
    for (const [name, value] of Object.entries(filters)) {
      if (['team_id', 'season_id', 'season_scope', 'club_id', 'actor_id', 'view'].includes(name)) throw new Error('collection_scope_override');
      if (value !== null && value !== undefined) query[name] = String(value);
    }
    const result = await transport.request<T>({ method: 'GET', path, query: { ...query, ...scope } });
    assertSafeWireNumbers(result);
    return result;
  }
  return {
    coach: (query: CoachFeedbackCollectionQuery = {}) => read<Schema['CoachFeedbackCollectionResponse']>('/api/coach/feedback', query),
    recipient: (query: RecipientFeedbackCollectionQuery = {}) => read<Schema['RecipientFeedbackCollectionResponse']>('/api/feedback', query),
  };
}
